import ConfirmRequestMessage from "./ConfirmRequestMessage";
import SecureEstablishedMessage from "./SecureEstablishedMessage";
import ReestablishSecureConnectionMessage from "./ReestablishSecureConnectionMessage";
import NormalMessage from "./NormalMessage";
import SystemMessage from "./SystemMessage";
import ImageMessage from "./ImageMessage";
import InviteRequestMessage from "./InviteRequestMessage";
import React, { useState, useRef, useEffect, useMemo, useLayoutEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { getConversationMetaData } from "../../db/ConversationMetadata";
import { usePasswordAndUserId } from "../../context/AuthContext";
import useOutsideClick from '../../utils/hooks/UseOutsideClick';
import { loadDecryptedMessages } from "../../db/Message";
import useConversationStore from "../../stores/ConversationStore";
import { uint8ArraysEqual } from "../../utils/CryptoHelpers";
import { formatDistanceToNow } from 'date-fns';
import { sendEncryptedMessage } from "../../services/message/SendEncryptedMessage";
import { v4 as uuidv4 } from 'uuid';
import bs58 from 'bs58';
import { getEnrichedParticipants, removeOwnViewKey, getParticipantsHandshakeStatus } from "../../services/Participants";
import { emojiList } from "../../constants/EmojiList";
import { MESSAGES_TYPES } from "../../Config";
import { loadLocalKeys } from "../../db/KeyStorageManager";
import { sendImageMessage } from "../../utils/ImageManager";
import { getMessageTargets } from "../../services/message/GetMessageTargets";
import ChatOptionsMenu from "./components/ChatOptionsMenu";
import { FixedSizeList as List } from "react-window";

/**
 * Memoized message renderer component.
 *
 * IMPORTANT: This must be defined outside of ChatView to prevent unnecessary re-creation
 * on every render. If it's defined inside ChatView, React treats it as a new component 
 * each time state (e.g., input) changes, causing all messages to re-render.
 */
const MemoizedMessageRenderer = React.memo(({ msg, idx, localKeys, participants, conversationId, forceShowInvite, forceShowReconnect, setShowMenu }) => {
  const { messageType } = msg;

  if (msg.sender === "system") {
    return <SystemMessage key={idx} message={msg} />;
  }

  if (messageType === 'system_invite_request') {
    return (
      <InviteRequestMessage
        key={msg.key}
        participant={msg.participant}
        localKeys={localKeys}
        conversationId={conversationId}
        fullScreen={false}
        canDismiss={forceShowInvite}
      />
    );
  }

  if (messageType === 'system_reconnect_request') {
    return (
      <ReestablishSecureConnectionMessage
        key={msg.key}
        participants={participants}
        conversationId={conversationId}
        localKeys={localKeys}
        fullScreen={false}
        canDismiss={forceShowReconnect}
      />
    );
  }

  const isMe = uint8ArraysEqual(msg.senderViewPublicKey, localKeys.viewPublicKey);

  if (messageType === MESSAGES_TYPES.MESSAGE_TYPE_HANDSHAKE_REQUEST) {
    return (
      <ConfirmRequestMessage
        key={idx}
        conversationId={conversationId}
        message={msg}
        localKeys={localKeys}
        participants={participants}
      />
    );
  }

  if (messageType === MESSAGES_TYPES.MESSAGE_TYPE_HANDSHAKE_CONFIRM) {
    return <SystemMessage key={idx} message="You're now securely chatting" />;
  }

  if (messageType === MESSAGES_TYPES.MESSAGE_TYPE_MEDIA) {
    return (
      <ImageMessage
        key={idx}
        conversationId={conversationId}
        message={msg}
        localKeys={localKeys}
        participants={participants}
      />
    );
  }

  return <NormalMessage key={idx} message={msg} isMe={isMe} time_human={msg.time_human} />;
});

export default function ChatView({ 
  conversationId,
  navigate, 
  onBack, 
}) {
  const [initialMessages, setInitialMessages] = useState([]);
  const [appendedMessages, setAppendedMessages] = useState([]);
  const [input, setInput] = useState("");
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [conversationMeta, setConversationMeta] = useState(null);
  const [participants, setParticipants] = useState(null);
  const [participantNames, setParticipantNames] = useState(null);
  const [participantAddresses, setParticipantAddresses] = useState(null);
  const [localKeys, setLocalKeys] = useState(false);
  const [forceShowInvite, setForceShowInvite] = useState(false);
  const [forceShowReconnect, setForceShowReconnect] = useState(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const attachmentMenuRef = useRef(null);
  const hasScrolledInitially = useRef(false);
  const lastAppendedMessageIdRef = useRef(null);
  const messagesContentRef = useRef(null);

  // auth
  const { password, userId, salt } = usePasswordAndUserId(); 

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const menuRef = useRef(null);
  const [hasReceivedMessage, setHasReceivedMessage] = useState(false);

  const latestMessage = useConversationStore(state => state.latestMessage);

  // outside click close
  useOutsideClick(menuRef, () => setShowMenu(false), setShowMenu);
  useOutsideClick(attachmentMenuRef, () => setShowAttachmentMenu(false), setShowAttachmentMenu);

  // scroll to bottom
  const scrollToBottom = (smooth = true) => {
    messagesEndRef.current?.scrollIntoView({
      behavior: smooth ? "smooth" : "auto",
    });
  };

  // load local keys
  useEffect(() => {
    (async () => {
      const keys = await loadLocalKeys({ password, userId, salt });
      setLocalKeys(keys);
    })();
  }, [password, userId, salt]);

  // get participants
  useEffect(() => {
    (async () => {
      if (!conversationId || !password || !localKeys) return;

      try {
        const meta = await getConversationMetaData({ conversationId, password });
        const participantKeys = removeOwnViewKey(meta.participants, localKeys.viewPublicKey);

        const enriched = await getEnrichedParticipants({
          participantKeys,
          userId,
          password,
        });

        const handshakeStatuses = await getParticipantsHandshakeStatus({
          participantKeys,
          conversationId,
          userId,
          password,
        });

        // Merge handshake status and keys into each participant
        const participants = enriched.map((p) => {
          const match = handshakeStatuses.find((s) =>
            uint8ArraysEqual(s.contactIdentityViewPublicKey, p.identityViewPublicKey)
          );

          return { ...p, keys: match };
        });

        const names = participants
          .map((c) => c.name || bs58.encode(c.identityViewPublicKey).slice(0, 10))
          .join(", ");

        const addresses = participants
          .map((c) => bs58.encode(c.identityViewPublicKey))
          .join(", ");

        setConversationMeta(meta);
        setParticipants(participants);
        setParticipantNames(names);
        setParticipantAddresses(addresses);
      } catch (err) {
        console.error("Failed to load metadata:", err);
      }
    })();
  }, [localKeys]);

  const handleSendMessage = async () => {
    if (!input.trim()) return;
    const message = input.trim();
    const tempId = uuidv4();

    setInput("");

    try {
      const targets = getMessageTargets(participants || []);  

      await Promise.all(
        targets.map((t) =>
          sendEncryptedMessage({
            conversationId,
            plaintext: message,
            contactIdentityViewPublicKey: t.identityViewPublicKey,
            contactNextViewPublicKey: t.contactNextViewPublicKey,
            kyberSecret: t.kyberSecret,
            userId,
            password,
            salt,
          })
        )
      );
    } catch (err) {
      console.error("Message failed to send:", err);
    }
  };

  // append new message
  useEffect(() => {
    if (
      !latestMessage ||
      !conversationId ||
      !participants
    ) return;

    const {
      receiverViewPublicKey,
      senderViewPublicKey,
      senderNextViewPublicKey,
      kyberKeys
    } = latestMessage;

    const updated = participants.map((p) => {
      const isSender = uint8ArraysEqual(p.identityViewPublicKey, senderViewPublicKey);
      const isReceiver = uint8ArraysEqual(p.identityViewPublicKey, receiverViewPublicKey);

      const newParticipant = { ...p, keys: { ...p.keys } };

      // Only update nextViewKey for sender
      if (
        isSender &&
        senderNextViewPublicKey &&
        (!p.contactNextViewPublicKey || !uint8ArraysEqual(p.contactNextViewPublicKey, senderNextViewPublicKey))
      ) {
        newParticipant.keys.contactNextViewPublicKey = senderNextViewPublicKey;
      }

      // Add kyber keys if this participant is sender or receiver
      if ((isSender || isReceiver) && kyberKeys?.kyberSecret) {
        const sessionKey = {
          kyberPublicKey: kyberKeys.kyberPublicKey,
          kyberSecret: kyberKeys.kyberSecret,
          kyberPrivateKey: undefined,
        };

        const existingKeys = p.keys?.sessionKyberKeys || [];

        // avoid duplication
        const alreadyExists = existingKeys.some(k =>
          uint8ArraysEqual(k.kyberPublicKey, kyberKeys.kyberPublicKey)
        );

        if (!alreadyExists) {
          newParticipant.keys.sessionKyberKeys = [...existingKeys, sessionKey];
        }
      }

      return newParticipant;
    });

    setParticipants(updated);
  }, [latestMessage]);

  // Append invite_request and reconnect pseudo-messages
  useEffect(() => {
    (async () => {
      if (!conversationId || !password || !localKeys || !participants) return;

      try {
        const storedMessages = await loadDecryptedMessages({ userId, conversationId, password });

        const hasReceivedMessage = storedMessages.some(
          msg => !uint8ArraysEqual(msg.senderViewPublicKey, localKeys.viewPublicKey)
        );
        setHasReceivedMessage(hasReceivedMessage);

        const inviteMessages = participants
          .filter(p => p.keys.status === "local_none__contact_none" || forceShowInvite)
          .map((p, i) => ({
            participant: p,
            key: `invite-${i}`,
            messageType: 'system_invite_request',
          }));

        const reconnectMessages = (forceShowReconnect)
          ? participants
            .map((p, i) => ({
              participant: p,
              key: `reconnect-${i}`,
              messageType: 'system_reconnect_request',
            }))
          : [];

        const finalMessages = [...storedMessages, ...inviteMessages, ...reconnectMessages]
        setInitialMessages(finalMessages);
        setAppendedMessages([]); // reset
      } catch (err) {
        console.error("Failed to load messages for conversation:", err);
      }
    })();
  }, [
    conversationId,
    password,
    localKeys,
    userId,
    participants,
    forceShowInvite,
    forceShowReconnect,
  ]);
  
  const allMessages = useMemo(() => [...initialMessages, ...appendedMessages], [initialMessages, appendedMessages]);

  // scroll to bottom
  useEffect(() => {
    const container = messagesContentRef.current;
    if (!container) return;

    // Auto-scroll when the container resizes (e.g., new messages change height)
    const resizeObserver = new ResizeObserver(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
    });

    resizeObserver.observe(container);

    // Scroll on message updates
    if (hasScrolledInitially.current) {
      scrollToBottom(true); // smooth scroll for subsequent updates
    } else {
      scrollToBottom(false); // initial jump scroll
      hasScrolledInitially.current = true;
    }

    return () => resizeObserver.disconnect();
  }, [allMessages]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    const onScroll = () => {
      const threshold = 100;
      const atBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
      setShowScrollButton(!atBottom);
    };
    container?.addEventListener("scroll", onScroll);
    return () => container?.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  if (!conversationMeta || !participants) return null;

  const handleEmojiClick = (emoji) => {
    setInput((prev) => prev + emoji);
    setShowEmojis(false);
  };

  return (
	  <div style={styles.container}>
	    <div style={styles.overlay} />

	    {/* Header */}
      <div style={styles.header}>
        <div style={styles.leftHeader}>
          <button onClick={onBack} style={styles.backButton}>←</button>
          <img src="/images/avatar.png" alt="avatar" style={styles.avatar} />
          <div style={styles.textContainer}>
            <div style={styles.name}>{participantNames}</div>
            <div style={styles.address}>{participantAddresses}</div>
          </div>
        </div>

        <button onClick={() => setShowMenu(!showMenu)} style={styles.menuButton}>⋮</button>

        {showMenu && (
          <ChatOptionsMenu
            onResendInvite={() => {
              setForceShowInvite(false);
              requestAnimationFrame(() => setForceShowInvite(true));
            }}
            onReEstablishConnection={() => {
              setForceShowReconnect(false);
              requestAnimationFrame(() => setForceShowReconnect(true));
            }}
            showReconnect={hasReceivedMessage}
            onClose={() => setShowMenu(false)}
            menuRef={menuRef}
          />
        )}
      </div>

	    {/* Messages */}
	    <div ref={messagesContainerRef} style={styles.messageList} className="hide-scroll">
	       <div ref={messagesContentRef} style={styles.messageListInner}>
          {allMessages
            .filter((msg, idx, arr) => {
              const isRequest = msg.messageType === MESSAGES_TYPES.MESSAGE_TYPE_HANDSHAKE_REQUEST;
              const isConfirm = msg.messageType === MESSAGES_TYPES.MESSAGE_TYPE_HANDSHAKE_CONFIRM;

              if (isRequest) {
                return !arr.slice(idx + 1).some(
                  m => m.messageType === MESSAGES_TYPES.MESSAGE_TYPE_HANDSHAKE_CONFIRM
                );
              }

              if (isConfirm) {
                const next = arr[idx + 1];
                return !next || next.messageType !== MESSAGES_TYPES.MESSAGE_TYPE_HANDSHAKE_CONFIRM;
              }

              return true;
            })
            .map((msg, idx) => (
              <MemoizedMessageRenderer
                key={msg.id || msg.key || idx}
                msg={msg}
                idx={idx}
                localKeys={localKeys}
                participants={participants}
                conversationId={conversationId}
                forceShowInvite={forceShowInvite}
                forceShowReconnect={forceShowReconnect}
                setShowMenu={setShowMenu}
              />
          ))}
        </div>

	      <div ref={messagesEndRef} />
	    </div>

	    {/* Input */}
	    <div style={styles.inputWrapper}>
	      {showScrollButton && (
	        <button onClick={scrollToBottom} style={styles.scrollDownButton}>↓</button>
	      )}
	      <div style={styles.inputBar}>
	        <button onClick={() => setShowEmojis(!showEmojis)} style={styles.emojiButton}>
	          <img src="images/emoji-icon.png" style={styles.emojiButtonEmoji} />
	        </button>
	        {showEmojis && (
	          <div style={styles.emojiMenu}>
	            {emojiList.map((emoji, index) => (
	              <span key={index} onClick={() => handleEmojiClick(emoji)} style={styles.emojiItem}>
	                {emoji}
	              </span>
	            ))}
	          </div>
	        )}
	        <textarea
	          ref={textareaRef}
	          style={styles.input}
	          value={input}
	          onChange={(e) => setInput(e.target.value)}
	          onKeyDown={(e) => {
	            if (e.key === "Enter" && !e.shiftKey) {
	              e.preventDefault();
	              handleSendMessage();
	            }
	          }}
	          placeholder={`Message ${participantNames}`}
	        />
          <div style={{ position: "relative" }}>
            <button onClick={() => setShowAttachmentMenu(!showAttachmentMenu)} style={styles.emojiButton}>
              <img src="images/icon-paperclip.png" style={styles.emojiButtonEmoji} />
            </button>

            {showAttachmentMenu && (
              <div ref={attachmentMenuRef} style={styles.attachmentMenu}>
                <label style={styles.attachmentItem}>
                  Attach Image (max 10KB)
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={async (e) => {
                      const file = e.target.files[0];

                      const targets = getMessageTargets(participants || []);  

                      await Promise.all(
                        targets.map((t) =>
                          sendImageMessage({
                            conversationId,
                            file,
                            contactIdentityViewPublicKey: t.identityViewPublicKey,
                            contactNextViewPublicKey: t.contactNextViewPublicKey,
                            kyberSecret: t.kyberSecret,
                            userId,
                            password,
                            salt,
                          })
                        )
                      );
                      setShowAttachmentMenu(false);
                    }}
                  />
                </label>
              </div>
            )}
          </div>
	        {input.trim().length > 0 && (
            <button onClick={handleSendMessage} style={styles.sendButton}>Send</button>
          )}
	      </div>
	    </div>
	  </div>
	);
}

const styles = {
  container: {
    position: "relative",
    height: "100vh",
    backgroundColor: "rgb(17, 17, 17)",
    backgroundImage: 'url("/images/background-tile.png")',
    backgroundRepeat: "repeat",
    backgroundSize: "200px auto",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
  },
  overlay: {
    position: "absolute",
    inset: 0,
    backgroundColor: "rgb(17, 17, 17)",
    opacity: 0.85,
    zIndex: 0,
  },
  header: {
    zIndex: 100,
    position: "relative",
    padding: "1rem",
    backgroundColor: "#111",
    borderBottom: "1px solid #333",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  leftHeader: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    flex: 1,
  },
  backButton: {
    color: "#fff",
    background: "none",
    border: "none",
    fontSize: "1.5rem",
    cursor: "pointer",
  },
  avatar: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    objectFit: "cover",
  },
  textContainer: {
    display: "flex",
    flexDirection: "column",
  },
  name: {
    fontSize: "1.2rem",
    fontWeight: "bold",
  },
  address: {
    fontSize: "0.75rem",
    color: "#666",
    marginTop: "0.2rem",
    wordBreak: "break-word",
  },
  menuButton: {
    background: "none",
    border: "none",
    color: "#aaa",
    fontSize: "1.4rem",
    cursor: "pointer",
  },
  qrLabel: {
    fontSize: "0.75rem",
    color: "#aaa",
    marginTop: "0.5rem",
    marginBottom: "1rem",
  },
  menuItemRed: {
    padding: "0.5rem",
    color: "#fff",
    cursor: "pointer",
    backgroundColor: "rgb(255, 102, 102)",
    borderRadius: "6px",
  },
  messageList: {
    zIndex: 1,
    position: "relative",
    flex: 1,
    padding: "1rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    overflowY: "auto",
  },
  messageListInner: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  message: {
    maxWidth: "70%",
    padding: "0.6rem 1rem",
    borderRadius: "12px",
    fontSize: "0.95rem",
    whiteSpace: "pre-wrap",
    position: "relative",
  },
  statusCheck: {
    fontSize: "0.75rem",
    color: "#ddd",
    marginTop: "0.25rem",
    textAlign: "right",
  },
  inputWrapper: {
    zIndex: 1,
    position: "relative",
    backgroundColor: "#111",
    borderTop: "1px solid #333",
  },
  scrollDownButton: {
    position: "absolute",
    right: "1rem",
    bottom: "100%",
    marginBottom: "0.5rem",
    backgroundColor: "#2a2a2a",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: "32px",
    height: "32px",
    fontSize: "1.2rem",
    cursor: "pointer",
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.5)",
  },
  inputBar: {
    display: "flex",
    padding: "0.5rem",
    alignItems: "flex-end",
  },
  input: {
    flex: 1,
    padding: "0.5rem",
    fontSize: "1rem",
    backgroundColor: "#222",
    color: "#fff",
    border: "1px solid #444",
    borderRadius: "6px",
    marginRight: "0.5rem",
    resize: "none",
    overflow: "hidden",
    lineHeight: "1.4",
    minHeight: "40px",
  },
  sendButton: {
    backgroundColor: "#4e8cff",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "0.5rem 1rem",
    cursor: "pointer",
  },
  emojiButton: {
    background: "none",
    border: "none",
    fontSize: "1.5rem",
    marginRight: "0.5rem",
    cursor: "pointer",
    color: "#ccc",
  },
  emojiButtonEmoji: {
    height: "40px",
    width: "40px",
    filter: "brightness(3.5)", 
  },
  emojiMenu: {
    position: "absolute",
    bottom: "3.5rem",
    left: "0.5rem",
    backgroundColor: "#222",
    border: "1px solid #444",
    borderRadius: "8px",
    padding: "0.5rem",
    display: "flex",
    flexWrap: "wrap",
    gap: "0.25rem",
    zIndex: 10,
  },
  emojiItem: {
    fontSize: "1.25rem",
    cursor: "pointer",
  },
  attachmentMenu: {
    position: "absolute",
    bottom: "2.5rem",
    right: 0,
    backgroundColor: "#222",
    border: "1px solid #444",
    borderRadius: "8px",
    padding: "0.5rem",
    zIndex: 10,
    minWidth: "160px",
    textAlign: "left",
  },
  attachmentItem: {
    color: "#fff",
    cursor: "pointer",
    fontSize: "0.9rem",
    padding: "0.3rem 0.5rem",
    display: "block",
  },
};